import React, { useEffect, useMemo, useRef, useState } from 'react';
import './style.css';
import { BackIcon } from '../../assets/icons/icons';
import useProfile from '../../hooks/useProfile';
import { supabase } from '../../supabaseClient';

export default function ProfileModal({ onClose }) {
  const { data, loading, error, refresh, updateBasics } = useProfile({ refreshOnMount: true });
  const profile = useMemo(() => data?.profile ?? null, [data]);
  const retried = useRef(false);

  const [form, setForm] = useState({
    name: '',
    age: '',
    gender: '',
    height_feet: '',
    height_inches: '',
    weight_lbs: '',
  });
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [anim, setAnim] = useState('enter'); // 'enter' | 'exit'

  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name ?? '',
        age: profile.age ?? '',
        gender: profile.gender ?? '',
        height_feet: profile.height_feet ?? '',
        height_inches: profile.height_inches ?? '',
        weight_lbs: profile.current_weight_lbs ?? profile.weight_lbs ?? '',
      });
      setEmail(profile.email ?? '');
    }
  }, [profile]);

  useEffect(() => {
    if (!loading && !error && !profile && !retried.current) {
      retried.current = true;
      refresh();
    }
  }, [loading, error, profile, refresh]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSaveBasics = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        height_feet: form.height_feet ? Number(form.height_feet) : null,
        height_inches: form.height_inches ? Number(form.height_inches) : null,
        weight_lbs: form.weight_lbs ? Number(form.weight_lbs) : null,
      };
      const res = await updateBasics(payload);
      if (res?.error) alert('Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setAnim('exit');
    // Match CSS animation duration (300ms) + small buffer
    setTimeout(() => onClose?.(), 340);
  };

  const goFullProfile = () => {
    setAnim('exit');
    setTimeout(() => { window.location.href = '/profile'; onClose?.(); }, 340);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (_) {}
    try {
      localStorage.removeItem('payment_complete');
      localStorage.removeItem('payment_session_id');
      localStorage.removeItem('selected_plan_id');
      localStorage.removeItem('selected_plan_name');
      localStorage.removeItem('onboarding_profile');
    } catch (_) {}
    handleClose();
    window.location.href = '/login';
  };

  return (
    <div className={`pm-overlay ${anim}`} onClick={handleClose}>
      <div className={`pm-panel ${anim}`} onClick={(e) => e.stopPropagation()}>
        <div className="pm-header">
          <button className="pm-back" onClick={handleClose} aria-label="Back">
            <BackIcon className="icon" />
          </button>
          <h3 className="pm-title">My Profile</h3>
          <div style={{ width: 36 }} />
        </div>

        <div className="pm-content">
          {(loading || (!profile && !error)) && <p>Loading profile...</p>}
          {error && <p>Error loading profile.</p>}
          {!loading && !error && profile && (
            <>
              <div className="pm-card">
                <div className="row">
                  <label>Email</label>
                  <input type="email" value={email} readOnly disabled />
                </div>
                <div className="row">
                  <label>Name</label>
                  <input name="name" value={form.name} readOnly disabled />
                </div>
                <div className="row">
                  <label>Age</label>
                  <input name="age" value={form.age} readOnly disabled />
                </div>
                <div className="row">
                  <label>Gender</label>
                  <input name="gender" value={form.gender} readOnly disabled />
                </div>
                <div className="row">
                  <label>Height (ft/in)</label>
                  <div className="row-inline">
                    <input name="height_feet" value={form.height_feet} readOnly disabled />
                    <input name="height_inches" value={form.height_inches} readOnly disabled />
                  </div>
                </div>
                <div className="row">
                  <label>Current Weight (lbs)</label>
                  <input name="weight_lbs" value={form.weight_lbs} readOnly disabled />
                </div>
              </div>
              <p className="pm-subtext">To edit your basics info, please go to the profile page.</p>
              <div className="pm-footer-actions">
                <button type="button" className="pm-full-btn" onClick={goFullProfile}>See full profile</button>
              </div>
              <div className="pm-footer-actions">
                <button type="button" className="pm-logout-btn" onClick={handleLogout}>Logout</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
